// @ts-nocheck
// Call-centre chooser for platform (super / sub-super admin) and company
// (reseller / company admin) viewers. Loads GET /live-calls/scopes once and
// shows one tile per call centre with its ZCARD totals from
// GET /live-calls/counts (refreshed every COUNTS_MS while visible).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { getApiCall } from '@/service/httpCall';
import { API_LIVE_CALLS_COUNTS, API_LIVE_CALLS_SCOPES } from '@/service/url';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { usePageVisible } from '@/hooks/usePageVisible';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/feedback/EmptyState';
import { HAIRLINE, MiniBar, nf } from '@/features/cti2/team/teamUi';
import type { LiveCallCallCentre, LiveCallCounts } from '../types';

const COUNTS_MS = 5000;

interface Props {
  selected: LiveCallCallCentre | null;
  onSelect: (cc: LiveCallCallCentre) => void;
}

export default function CallCentrePicker({ selected, onSelect }: Props) {
  const [centres, setCentres] = useState<LiveCallCallCentre[] | null>(null);
  const [counts, setCounts] = useState<Record<number, LiveCallCounts>>({});
  const [query, setQuery] = useState('');
  const visible = usePageVisible();

  useEffect(() => {
    getApiCall(API_LIVE_CALLS_SCOPES)
      .then((data) => {
        const list = Array.isArray(data?.callCentres) ? data.callCentres : [];
        setCentres(list);
        if (!selected && list.length === 1) onSelect(list[0]);
      })
      .catch((err) => {
        setCentres([]);
        toast.error(friendlyApiError(err) || 'Unable to load call centres');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshCounts = useCallback(async () => {
    if (!centres?.length) return;
    const results = await Promise.allSettled(
      centres.map((cc) =>
        getApiCall(API_LIVE_CALLS_COUNTS, { companyId: cc.companyId, ccId: cc.ccId }).then((d) => [cc.ccId, d?.counts]),
      ),
    );
    const next = {};
    results.forEach((r) => {
      if (r.status === 'fulfilled' && r.value?.[1]) next[r.value[0]] = r.value[1];
    });
    setCounts((prev) => ({ ...prev, ...next }));
  }, [centres]);

  useEffect(() => {
    if (!visible || !centres?.length) return undefined;
    refreshCounts();
    const t = setInterval(refreshCounts, COUNTS_MS);
    return () => clearInterval(t);
  }, [visible, centres, refreshCounts]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = centres || [];
    return q ? list.filter((c) => c.name?.toLowerCase().includes(q) || String(c.ccId).includes(q)) : list;
  }, [centres, query]);

  if (centres === null) {
    return (
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-[92px] rounded-2xl" />)}
      </div>
    );
  }
  if (!centres.length) {
    return <EmptyState icon={Building2} title="No call centres" description="Nothing to monitor under this account yet." />;
  }

  return (
    <div className="space-y-2.5">
      {centres.length > 6 ? (
        <div className="relative max-w-xs">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input id="live-calls-cc-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a call centre" className="h-8 pl-8 text-xs" />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-5">
        {shown.map((cc) => {
          const k = counts[cc.ccId];
          const total = k ? k.total : null;
          const active = selected?.ccId === cc.ccId;
          return (
            <button
              key={cc.ccId}
              type="button"
              onClick={() => onSelect(cc)}
              className={`rounded-2xl border bg-card p-3 text-left shadow-sm transition-colors hover:bg-muted/40 ${HAIRLINE} ${active ? 'ring-2 ring-primary/40' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="truncate text-[12.5px] font-semibold">{cc.name || `Call centre ${cc.ccId}`}</span>
                <span className="shrink-0 text-[10px] text-muted-foreground">cc {cc.ccId}</span>
              </div>
              <div className="mt-1.5 text-[22px] font-semibold leading-none tabular-nums">{total === null ? '—' : nf(total)}</div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">in <MiniBar value={k?.in || 0} max={Math.max(total || 1, 1)} tone="call" width={28} /> <b className="tabular-nums">{k ? nf(k.in) : '—'}</b></span>
                <span className="inline-flex items-center gap-1">out <MiniBar value={k?.out || 0} max={Math.max(total || 1, 1)} tone="good" width={28} /> <b className="tabular-nums">{k ? nf(k.out) : '—'}</b></span>
                <span className="inline-flex items-center gap-1">int <MiniBar value={k?.int || 0} max={Math.max(total || 1, 1)} tone="info" width={28} /> <b className="tabular-nums">{k ? nf(k.int) : '—'}</b></span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
